// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IsPlainObject<T> = T extends any[]
  ? false
  : // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    T extends Function
    ? false
    : T extends object
      ? true
      : false;

/**
 * Removes specified keys from a plain object and returns a new object without those keys.
 *
 * @template T - The type of the input object. Must be a plain object.
 * @template K - The keys of the input object to omit.
 * @param {T} obj - The plain object from which keys will be omitted. Must not be an array, function, or null.
 * @param {K[]} keysToOmit - An array of keys to omit from the object.
 * @returns {Omit<T, K>} A new object with the specified keys omitted.
 * @throws {TypeError} If the input is not a plain object.
 *
 * @example
 * const user = { id: 1, name: 'John', password: 'secret' };
 * const result = objectOmit(user, ['password']);
 * console.log(result); // Output: { id: 1, name: 'John' }
 */

export const objectOmit = <T extends object, K extends keyof T>(
  obj: IsPlainObject<T> extends true ? T : never,
  keysToOmit: K[],
): Omit<T, K> => {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj) || typeof obj === 'function') {
    throw new TypeError('Expected a plain object', { cause: obj });
  }

  const result = {} as Omit<T, K>;

  Object.keys(obj).forEach(key => {
    if (Object.prototype.hasOwnProperty.call(obj, key) && !keysToOmit.includes(key as K)) {
      result[key as Exclude<keyof T, K>] = obj[key as Exclude<keyof T, K>];
    }
  });

  return result;
};
